"use client";

import { iconSizePresets } from "@/shared/config/iconSizePresets";
import {
  Box,
  Button,
  useDisclosure,
  Textarea,
  useClipboard,
  Tokens,
} from "@chakra-ui/react";
import { useColorModeValue } from "../color-mode";
import {
  useRef,
  ReactElement,
  useCallback,
  memo,
  useState,
  useEffect,
} from "react";
import { DeleteMode } from "@/generated/fastapi";
import GenericIcon, {
  type GenericIconType,
  type ResponsiveIconSize,
} from "./GenericIcon";
import LoadingState from "../LoadingState";
import { showSuccessToast } from "@/shared/feedback";
import { useSystemDesign } from "@/shared/system-design";
import { getActionToneBg } from "./actionTone";

/**
 * WithControlButtons
 *
 * A flexible wrapper component that adds action buttons, edit mode, clipboard,
 * and loading overlay capabilities to child content. Buttons appear on
 * focus/hover with smart positioning.
 *
 * @example
 * // Simple action + delete with accessibility
 * <WithControlButtons
 *   primary={{
 *     onClick: handleInspect,
 *     icon: "insight",
 *     humanReadable: "Inspect thought details"
 *   }}
 *   delete={{
 *     onClick: handleDelete,
 *     mode: "permanent",
 *     humanReadable: "Delete thought permanently"
 *   }}
 * >
 *   <Text>Content here</Text>
 * </WithControlButtons>
 *
 * @example
 * // Conditional button visibility with hide flag
 * <WithControlButtons
 *   primary={{
 *     onClick: handleSave,
 *     icon: "save",
 *     hide: !hasChanges, // Only show when there are changes
 *     humanReadable: "Save changes"
 *   }}
 *   secondary={{
 *     onClick: handleDiscard,
 *     icon: "close",
 *     hide: !hasChanges,
 *     humanReadable: "Discard changes"
 *   }}
 * >
 *   <Text>Editable content</Text>
 * </WithControlButtons>
 *
 * @example
 * // Full-featured with edit mode
 * <WithControlButtons
 *   primary={{
 *     onClick: handleInspect,
 *     icon: "insight",
 *     humanReadable: "View thought insights"
 *   }}
 *   secondary={{
 *     onClick: handleExtract,
 *     icon: "extract",
 *     loading: isExtracting,
 *     humanReadable: "Extract related thoughts"
 *   }}
 *   delete={{
 *     onClick: handleDelete,
 *     mode: "soft",
 *     humanReadable: "Move to trash"
 *   }}
 *   edit={{
 *     onSave: handleSave,
 *     initialValue: thought.content,
 *     placeholder: "Write your thought...",
 *     onCancel: handleCancel,
 *     humanReadable: "Edit thought content"
 *   }}
 *   clipboard={{
 *     value: thought.content,
 *     humanReadable: "Copy thought to clipboard"
 *   }}
 *   loading={{ show: isPending, message: "Processing..." }}
 * >
 *   <Highlight attribute="content" hit={hit} />
 * </WithControlButtons>
 *
 * @example
 * // Clipboard-only (minimal) - humanReadable has default fallback
 * <WithControlButtons
 *   clipboard={{ value: target.content }}
 *   showChildrenBorderBase={false}
 * >
 *   <Text>{target.content}</Text>
 * </WithControlButtons>
 */

// ---------------------------------
// Utility: calculate icon positions based on presence
// ---------------------------------
type IconPresence = {
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  edit?: boolean;
  copyClipboard?: boolean;
};

const ICON_BUTTON_SPACING_PX = 56;
const ICON_BUTTON_START_RIGHT_PX = 8;
const OVERLAY_BUTTON_Z_INDEX = 900;
const LOADING_OVERLAY_Z_INDEX = 9999;
const OVERLAY_BUTTON_TOP = "2";
const OVERLAY_BUTTON_LEFT = "2";
const SHOW_ICONS_ON_HOVER_ON_DESKTOP = true;
const CLIPBOARD_IDLE_BG = "moveon.dismissLight";
const CLIPBOARD_SUCCESS_BG = "moveon.processed";
const PRIMARY_ACTION_BG = "moveon.primaryLight";
const SECONDARY_ACTION_BG = "moveon.secondaryLight";
const TERTIARY_ACTION_BG = "moveon.tertiaryLight";
const EDIT_ACTION_BG = "moveon.warningLight";
const CANCEL_EDIT_LABEL = "Cancel editing";
const CONFIRM_EDIT_LABEL = "Confirm editing";

function calculateIconPositions(icons: IconPresence): Record<string, string> {
  // Intentional px positioning: the overlay button rail uses exact offsets.
  let currentRight = ICON_BUTTON_START_RIGHT_PX;

  const positions: Record<string, string> = {};

  const iconOrder: (keyof IconPresence)[] = [
    "primary",
    "secondary",
    "tertiary",
    "edit",
    "copyClipboard",
  ];

  for (const icon of iconOrder) {
    if (icons[icon]) {
      positions[icon] = `${currentRight}px`;
      currentRight += ICON_BUTTON_SPACING_PX;
    }
  }

  return positions;
}

// ---------------------------------
// Nested config types for each button/feature
// ---------------------------------

type ActionButtonConfig = {
  onClick: () => Promise<void> | void;
  icon: GenericIconType;
  loading?: boolean;
  hide?: boolean; // Control rendering without undefining entire object
  humanReadable?: string; // Accessibility: title & aria-label (encouraged to set)
};

type DeleteButtonConfig = {
  onClick: () => Promise<void> | void;
  mode?: DeleteMode | "close"; // allow "close" for temporary states deletion (not in backend)
  hide?: boolean;
  humanReadable?: string; // e.g., "Delete thought permanently"
};

type EditConfig = {
  onSave: (value: string) => Promise<void> | void;
  initialValue: string;
  placeholder?: string;
  icon?: GenericIconType;
  onCancel?: () => void;
  hide?: boolean;
  humanReadable?: string; // e.g., "Edit thought content"
};

type ClipboardConfig = {
  value: string;
  onCopy?: (value: string) => void;
  hide?: boolean;
  humanReadable?: string; // e.g., "Copy to clipboard"
};

type LoadingOverlayConfig = {
  show: boolean;
  message?: string;
};

type OverlayActionButtonProps = {
  top?: string;
  left?: string;
  right?: string;
  bg?: Tokens["colors"] | string;
  icon: GenericIconType;
  iconSize: ResponsiveIconSize;
  humanReadable?: string;
  loading?: boolean;
  onMouseDown: (event: React.MouseEvent) => void | Promise<void>;
};

type OverlayActionDefinition = {
  key: string;
  top?: string;
  left?: string;
  right?: string;
  bg?: Tokens["colors"] | string;
  icon: GenericIconType;
  humanReadable?: string;
  loading?: boolean;
  onMouseDown: (event: React.MouseEvent) => void | Promise<void>;
};

type BuildOverlayActionsArgs = {
  isEditMode: boolean;
  iconPositions: Record<string, string>;
  buttonSize: ResponsiveIconSize;
  clipboardCopied: boolean;
  primary?: ActionButtonConfig;
  secondary?: ActionButtonConfig;
  tertiary?: ActionButtonConfig;
  deleteConfig?: DeleteButtonConfig;
  edit?: EditConfig;
  clipboard?: ClipboardConfig;
  handlePrimaryAction: (event: React.MouseEvent) => void | Promise<void>;
  handleSecondaryAction: (event: React.MouseEvent) => void | Promise<void>;
  handleTertiaryAction: (event: React.MouseEvent) => void | Promise<void>;
  handleDelete: (event: React.MouseEvent) => void | Promise<void>;
  handleEnterEditMode: (event: React.MouseEvent) => void | Promise<void>;
  handleCancelEdit: (event: React.MouseEvent) => void | Promise<void>;
  handleConfirmEdit: (event: React.MouseEvent) => void | Promise<void>;
  handleCopyClipboard: (event: React.MouseEvent) => void | Promise<void>;
};

function getDeleteActionIcon(
  mode: DeleteMode | "close" | undefined,
): GenericIconType {
  if (mode === "permanent") return "delete_permanent";
  if (mode === "soft") return "delete_soft";
  return "close";
}

function getClipboardLabel(
  humanReadable: string | undefined,
  copied: boolean,
): string {
  if (humanReadable) {
    return humanReadable;
  }

  return copied ? "Copied!" : "Copy to clipboard";
}

function containsNode(
  ref: React.RefObject<HTMLElement | null>,
  next: Node | null,
): boolean {
  return Boolean(ref.current && next && ref.current.contains(next));
}

function buildOverlayActions({
  isEditMode,
  iconPositions,
  clipboardCopied,
  primary,
  secondary,
  tertiary,
  deleteConfig,
  edit,
  clipboard,
  handlePrimaryAction,
  handleSecondaryAction,
  handleTertiaryAction,
  handleDelete,
  handleEnterEditMode,
  handleCancelEdit,
  handleConfirmEdit,
  handleCopyClipboard,
}: BuildOverlayActionsArgs): OverlayActionDefinition[] {
  if (isEditMode) {
    return [
      {
        key: "cancel-edit",
        right: "14",
        bg: SECONDARY_ACTION_BG,
        icon: "close",
        humanReadable: CANCEL_EDIT_LABEL,
        onMouseDown: handleCancelEdit,
      },
      {
        key: "confirm-edit",
        right: "2",
        bg: PRIMARY_ACTION_BG,
        icon: "confirm",
        humanReadable: CONFIRM_EDIT_LABEL,
        onMouseDown: handleConfirmEdit,
      },
    ];
  }

  const overlayActions: OverlayActionDefinition[] = [];

  if (deleteConfig && !deleteConfig.hide) {
    overlayActions.push({
      key: "delete",
      left: OVERLAY_BUTTON_LEFT,
      bg: getActionToneBg("error"),
      icon: getDeleteActionIcon(deleteConfig.mode),
      humanReadable: deleteConfig.humanReadable,
      onMouseDown: handleDelete,
    });
  }

  if (primary && !primary.hide) {
    overlayActions.push({
      key: "primary",
      right: iconPositions.primary,
      bg: PRIMARY_ACTION_BG,
      icon: primary.icon,
      loading: primary.loading,
      humanReadable: primary.humanReadable,
      onMouseDown: handlePrimaryAction,
    });
  }

  if (secondary && !secondary.hide) {
    overlayActions.push({
      key: "secondary",
      right: iconPositions.secondary,
      bg: SECONDARY_ACTION_BG,
      icon: secondary.icon,
      loading: secondary.loading,
      humanReadable: secondary.humanReadable,
      onMouseDown: handleSecondaryAction,
    });
  }

  if (tertiary && !tertiary.hide) {
    overlayActions.push({
      key: "tertiary",
      right: iconPositions.tertiary,
      bg: TERTIARY_ACTION_BG,
      icon: tertiary.icon,
      loading: tertiary.loading,
      humanReadable: tertiary.humanReadable,
      onMouseDown: handleTertiaryAction,
    });
  }

  if (edit && !edit.hide) {
    overlayActions.push({
      key: "edit",
      right: iconPositions.edit,
      bg: EDIT_ACTION_BG,
      icon: edit.icon || "edit",
      humanReadable: edit.humanReadable,
      onMouseDown: handleEnterEditMode,
    });
  }

  if (clipboard && !clipboard.hide) {
    overlayActions.push({
      key: "clipboard",
      right: iconPositions.copyClipboard,
      bg: clipboardCopied ? CLIPBOARD_SUCCESS_BG : CLIPBOARD_IDLE_BG,
      icon: clipboardCopied ? "confirm" : "copy_clipboard",
      humanReadable: getClipboardLabel(
        clipboard.humanReadable,
        clipboardCopied,
      ),
      onMouseDown: handleCopyClipboard,
    });
  }

  return overlayActions;
}

function OverlayActionButton({
  top = OVERLAY_BUTTON_TOP,
  left,
  right,
  bg,
  icon,
  iconSize,
  humanReadable,
  loading,
  onMouseDown,
}: OverlayActionButtonProps) {
  return (
    <Box
      position="absolute"
      top={top}
      left={left}
      right={right}
      zIndex={OVERLAY_BUTTON_Z_INDEX}
    >
      <Button
        size="xs"
        onMouseDown={onMouseDown}
        bg={bg}
        loading={loading}
        title={humanReadable}
        aria-label={humanReadable}
      >
        <GenericIcon icon={icon} size={iconSize} />
      </Button>
    </Box>
  );
}

export type WithControlButtonsProps<T extends HTMLElement> = {
  children: ReactElement<React.HTMLAttributes<T> & React.RefAttributes<T>>;

  // Root level - general/visual props
  childWrapperProps?: React.ComponentProps<typeof Box>;
  buttonSize?: ResponsiveIconSize;
  childrenBorderBase?: "sm" | "md" | "lg";
  childrenBorderColor?: string;
  showChildrenBorderBaseOnFocus?: boolean;
  minH?: Tokens["sizes"];
  onBlurAction?: () => void;

  // Nested - each button/feature group
  primary?: ActionButtonConfig;
  secondary?: ActionButtonConfig;
  tertiary?: ActionButtonConfig;
  delete?: DeleteButtonConfig;
  edit?: EditConfig;
  clipboard?: ClipboardConfig;
  loading?: LoadingOverlayConfig;
};

function WithControlButtons<T extends HTMLElement>({
  children,
  childWrapperProps,
  // Root level - general props
  buttonSize = iconSizePresets.touchTarget,
  childrenBorderBase = "sm",
  childrenBorderColor = "moveon.inactive",
  showChildrenBorderBaseOnFocus = true,
  minH = "12",
  onBlurAction,
  // Nested - each button/feature group
  primary,
  secondary,
  tertiary,
  delete: deleteConfig,
  edit,
  clipboard,
  loading,
}: WithControlButtonsProps<T>) {
  // spinner focus ref and overlay bg
  const spinnerRef = useRef<HTMLDivElement | null>(null);
  const overlayBg = useColorModeValue(
    "rgba(255,255,255,0.55)",
    "rgba(0,0,0,0.35)",
  ) as Tokens["colors"];

  const showLoadingState = loading?.show ?? false;

  const { isMobileDevice } = useSystemDesign();

  useEffect(() => {
    if (showLoadingState) {
      // move keyboard focus to spinner for accessibility
      setTimeout(() => {
        spinnerRef.current?.focus();
      }, 0);
    }
  }, [showLoadingState]);

  // Import hooks
  const { open, onOpen, onClose, setOpen } = useDisclosure();

  // Refs
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Container ref to detect mouse/focus movements to action buttons (absolutely positioned siblings)
  const containerRef = useRef<HTMLDivElement>(null);

  // Local state: edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValue, setEditValue] = useState(edit?.initialValue || "");

  const iconPositions = calculateIconPositions({
    primary: !!primary && !primary.hide,
    secondary: !!secondary && !secondary.hide,
    tertiary: !!tertiary && !tertiary.hide,
    edit: !!edit && !edit.hide,
    copyClipboard: !!clipboard && !clipboard.hide,
  });

  // Clipboard hook
  const clipboardHook = useClipboard({
    value: clipboard?.value || "",
    timeout: 2000,
  });

  const handlePrimaryAction = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await primary?.onClick();
      setOpen(false);
    },
    [primary, setOpen],
  );

  const handleSecondaryAction = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await secondary?.onClick();
      setOpen(false);
    },
    [secondary, setOpen],
  );

  const handleTertiaryAction = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await tertiary?.onClick();
      setOpen(false);
    },
    [tertiary, setOpen],
  );

  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await deleteConfig?.onClick();
      setOpen(false);
    },
    [deleteConfig, setOpen],
  );

  // Edit mode handlers
  const handleEnterEditMode = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setEditValue(edit?.initialValue || "");
      setIsEditMode(true);
    },
    [edit?.initialValue],
  );

  const handleConfirmEdit = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await edit?.onSave(editValue);
      setIsEditMode(false);
      setOpen(false);
    },
    [edit, editValue, setOpen],
  );

  const handleCancelEdit = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setEditValue(edit?.initialValue || "");
      setIsEditMode(false);
      edit?.onCancel?.();
      setOpen(false);
    },
    [edit, setOpen],
  );

  // Copy to clipboard handler
  const handleCopyClipboard = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      clipboardHook.copy();
      clipboard?.onCopy?.(clipboard.value);
      showSuccessToast("Copied to clipboard");
      setOpen(false);
    },
    [clipboardHook, clipboard, setOpen],
  );

  const overlayActionButtons = buildOverlayActions({
    isEditMode,
    iconPositions,
    buttonSize,
    clipboardCopied: clipboardHook.copied,
    primary,
    secondary,
    tertiary,
    deleteConfig,
    edit,
    clipboard,
    handlePrimaryAction,
    handleSecondaryAction,
    handleTertiaryAction,
    handleDelete,
    handleEnterEditMode,
    handleCancelEdit,
    handleConfirmEdit,
    handleCopyClipboard,
  });

  // onBlur handler that uses relatedTarget to check whether the new focus is inside the wrapper.
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (containsNode(wrapperRef, next) || containsNode(containerRef, next)) {
        return;
      }
      if (isEditMode) {
        setEditValue(edit?.initialValue || "");
        setIsEditMode(false);
        edit?.onCancel?.();
      }
      onClose();
      onBlurAction?.();
    },
    [onClose, onBlurAction, isEditMode, edit],
  );

  return (
    <Box
      position="relative"
      ref={containerRef}
      style={{
        flex: 1,
      }}
    >
      {open && (
        overlayActionButtons.map((actionButton) => (
          <OverlayActionButton
            key={actionButton.key}
            top={actionButton.top}
            left={actionButton.left}
            right={actionButton.right}
            bg={actionButton.bg}
            icon={actionButton.icon}
            iconSize={buttonSize}
            humanReadable={actionButton.humanReadable}
            loading={actionButton.loading}
            onMouseDown={actionButton.onMouseDown}
          />
        ))
      )}

      {/* Child wrapper with default frame styling and focus handlers */}
      <Box
        // @ts-ignore Chakra complains about boxShadow
        _hover={
          showChildrenBorderBaseOnFocus
            ? {
                shadow: "lg", //! subtle shadow works, but when WithSwipeSelector is called on top, the shadow gets cut off
                borderRadius: "md",
                transition: "box-shadow 0.2s",
              }
            : {}
        }
        ref={wrapperRef}
        border={childrenBorderBase}
        // @ts-ignore Chakra complains about borderColor
        borderColor={childrenBorderBase ? childrenBorderColor : undefined}
        borderRadius={childrenBorderBase ? "md" : undefined}
        p="2"
        fontSize="sm"
        tabIndex={0}
        aria-hidden={showLoadingState}
        aria-busy={showLoadingState}
        pointerEvents={showLoadingState ? "none" : undefined}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            e.currentTarget.focus();
          }
        }}
        onFocus={() => {
          onOpen();
        }}
        onBlur={handleBlur}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setOpen(true);
          }
        }}
        onMouseEnter={() => {
          if (SHOW_ICONS_ON_HOVER_ON_DESKTOP && !isMobileDevice) {
            onOpen();
          }
        }}
        onMouseLeave={(e) => {
          if (SHOW_ICONS_ON_HOVER_ON_DESKTOP && !isMobileDevice) {
            const next = (e.relatedTarget as Node) || null;
            // If pointer moved to an element inside the container (e.g., action button), keep controls open
            if (
              containerRef.current &&
              next &&
              next instanceof Node &&
              containerRef.current.contains(next)
            ) {
              return;
            }
            onClose();
          }
        }}
        minH={minH}
        {...childWrapperProps}
      >
        {isEditMode && edit ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === "Enter") {
                e.preventDefault();
                handleConfirmEdit(e as unknown as React.MouseEvent);
              }
            }}
            placeholder={edit.placeholder || "Enter text..."}
            size="xs"
            fontSize="sm"
            autoresize
            // Entering edit mode is a direct user action, so focusing the
            // textarea here is intentional and not drawer-load autofocus.
            autoFocus
            style={{
              outline: "none",
              border: "none",
            }}
          />
        ) : (
          children
        )}
        {showLoadingState && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={LOADING_OVERLAY_Z_INDEX}
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="auto"
            // @ts-ignore Chakra complains about transition
            transition="opacity 180ms ease"
            bg={overlayBg}
            css={{
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              "@supports not ((-webkit-backdrop-filter: blur(6px)) or (backdrop-filter: blur(6px)))":
                {
                  backgroundColor: overlayBg,
                },
            }}
          >
            <LoadingState
              message={loading?.message || undefined}
              variant="inline"
              spinnerRef={spinnerRef}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default memo(WithControlButtons);
